import { useAuth0 } from '@auth0/auth0-react';
import { Box, Container, Toolbar, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { NavProvider } from '../../contexts/nav.context';
import { useNav } from '../../hooks/useNav';
import { LayoutProps, MainLayoutProps } from '../../types/types';
import Drawer from './Drawer/Drawer';
import Header from './Header/Header';
import InactivityPrompt from './InactivityPrompt';

export const MainLayout: React.FC<MainLayoutProps> = ({
  appBarColor,
  appTitle,
  drawerWidth = 260,
  footerContent,
  headerContent,
  headerLogo,
  enableAdminProxyBanner,
  inactivityPromptBeforeIdle,
  inactivityTimeout,
  initialDrawerOpen = true,
  initialOpenItem,
  logo,
  logoClosed,
  navItems,
  notification,
  onLogout,
}) => {
  return (
    <NavProvider
      navItems={navItems}
      initialOpenItem={initialOpenItem}
      initialDrawerOpen={initialDrawerOpen}
    >
      <Layout
        appBarColor={appBarColor}
        appTitle={appTitle}
        drawerWidth={drawerWidth}
        footerContent={footerContent}
        headerContent={headerContent}
        enableAdminProxyBanner={enableAdminProxyBanner}
        inactivityPromptBeforeIdle={inactivityPromptBeforeIdle}
        inactivityTimeout={inactivityTimeout}
        initialDrawerOpen={initialDrawerOpen}
        logo={logo}
        logoClosed={logoClosed}
        headerLogo={headerLogo}
        notification={notification}
        onLogout={onLogout}
      />
    </NavProvider>
  );
};

const Layout = ({
  appBarColor,
  appTitle,
  drawerWidth = 260,
  enableAdminProxyBanner = false,
  footerContent,
  headerContent,
  inactivityPromptBeforeIdle,
  inactivityTimeout,
  initialDrawerOpen = true,
  logo,
  logoClosed,
  headerLogo,
  notification,
  onLogout,
}: LayoutProps) => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const { fluid: isFluid, setDrawerOpen } = useNav();
  const matchDownXL = useMediaQuery(theme.breakpoints.down('xl'));
  const { user: auth0User } = useAuth0();

  // Check if user is an admin proxying in
  const isAdminProxying = auth0User?.sub?.startsWith('okta|armhr-ops');

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Set media-wise responsive drawer, but respect initialDrawerOpen
  useEffect(() => {
    if (initialDrawerOpen) {
      setDrawerOpen(!matchDownXL);
    }
  }, [matchDownXL, setDrawerOpen, initialDrawerOpen]);

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      <Header
        appBarColor={appBarColor}
        drawerWidth={drawerWidth}
        headerContent={headerContent}
        enableAdminProxyBanner={enableAdminProxyBanner}
        logo={headerLogo || logo}
      />
      <Drawer
        logo={logo}
        logoClosed={logoClosed}
        appTitle={appTitle}
        drawerWidth={drawerWidth}
      />
      <Box
        component="main"
        sx={{
          background: theme.sidebar?.contentBackground || 'white',
          width: `calc(100% - ${drawerWidth}px)`,
          flexGrow: 1,
        }}
      >
        <Toolbar />
        {isAdminProxying && enableAdminProxyBanner && (
          <Toolbar sx={{ minHeight: '48px !important' }} />
        )}
        {notification}
        {onLogout && inactivityTimeout && inactivityPromptBeforeIdle && (
          <InactivityPrompt
            onLogout={onLogout}
            promptBeforeIdle={inactivityPromptBeforeIdle}
            timeout={inactivityTimeout}
          />
        )}
        {isFluid ? (
          <Container
            maxWidth={false}
            sx={{
              p: '0 !important',
              m: 0,
              position: 'relative',
              maxHeight: 'calc(100vh - 60px)',
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '100%',
            }}
          >
            <Outlet />
          </Container>
        ) : (
          <Container
            maxWidth="lg"
            sx={{
              px: { xs: 2, sm: 2 },
              py: { xs: 2, md: 3 },
              position: 'relative',
              minHeight: 'calc(100vh - 60px)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Outlet />
            {footerContent}
          </Container>
        )}
      </Box>
    </Box>
  );
};

export default MainLayout;
